import { JsonApiObject, JsonApiRelationships, JsonApiResponse } from 'json-api-normalizer';

import { ActionType, Processes, StepName, StepNames } from '../../design-system/types';
import { AgentId } from '../agent/types';
import { DisplayProject } from '../dossier-overview/types';
import { InTripReview, InTripReviewFromAPI, NormalizedInTripReview } from '../in-trip-review/types';
import {
    NormalizedPreTripReview,
    PreTripReview,
    PreTripReviewFromAPI,
} from '../pre-trip-review/types';
import { IncludedTag } from '../tags/api/api';

export type DossierId = string;
export type DossierOverview = {
    id: string;
    step: StepName;
    process: Processes;
    rating: number;
    budget: null | number;
    task: Task;
    project: DisplayProject;
    allowedChangeStepsTo: StepNames;
    discussion: ExchangeDiscussion;
    testimonialReceptionDate?: Date | null;
    archiveDate?: Date | null;
    agentId: AgentId;
    requestDate: Date;
    contract: ContractFromDossier;
    requesterIsRepeater: boolean;
    isDuplicate: boolean;
    duplicateState: DuplicateState | null;
    duplicateFromDossierId: DossierId | null;
    heatLevel: Heat;
    tags: DossierTag[];
};

export type DossierTag = {
    id: string;
    name: string;
    color: string;
};

export enum Heat {
    COLD = 0,
    NONE = 1,
    WARM = 2,
    HOT = 3,
}

export type DuplicateState = 'found' | 'confirmed' | 'ignored';
export type TaskNote = string | null;
export type Memo = string | null;
export type TaskDefinition = string;
export type Task = {
    dueDate: Date;
    closeDate?: Date | null;
    definition: TaskDefinition;
    actionType: ActionType;
    note: TaskNote;
};
export type Project = {
    requesterId: number;
    requesterName: string;
    requesterPhone?: string | null;
    arrivalDate?: string | null;
    departureDate?: string | null;
    approximateArrivalDate?: string | null;
    approximateDurationInDays?: number | null;
    paxNumber?: number | null;
    idealBudget?: {
        amountPerPax: number;
        currency: string;
        totalInEuro: number;
    } | null;
    maxTotalInEuro?: number | null;
    destinationLabel: string;
    marketCode: MarketCode;
    maturityExperiments?: {
        isLatencyTest?: boolean;
        at1MessageDate?: Date | null;
        tripProjectId?: string /* Maturity AB test ab_at1_feedback PR #2501 */;
    } | null;
};
export type ExchangeDiscussion = {
    lastTravellerToAgentMessageDate: Date | null;
    lastAgentToTravellerMessageDate: Date | null;
    lastMessageReadDate: Date | null;
    hasResponseRequired: boolean;
    hasUnreadMessages: boolean | null;
    lastTravellerUnansweredMessageId: number | null;
    lastTravellerUnansweredMessageContent: string | null;
};
export type ContractFromDossier = { state: ContractIssueStatus };
export type ContractIssueStatus = 'issued' | 'not-issued' | 'unknown';
export type NormalizedDossier = {
    [key in DossierId]: DossierOverview;
};

export interface DossierOverviewWithInbox extends DossierOverview {
    readAt: Date | null;
    sendDate: Date;
}

export type InitialRequest = {
    itineraryUrl: string | null;
    summary: string | null;
};

type Profiles = string;
export type ChildrenAges = Array<number>;

export type Participants = {
    profile: Profiles;
    number: {
        adults: number;
        teenagers: number;
        children: number;
        babies: number;
    };
    childrenAges: {
        teenagers: ChildrenAges | null;
        children: ChildrenAges | null;
        babies: ChildrenAges | null;
    } | null;
};

export type CustomerCareCrisisTicket = {
    url: string;
};

export type Voucher = {
    id: string;
    filename: string;
};

export type ViewVoucher = {
    id: string;
    filename: string;
    url: string;
};

export type DeferAgreement = {
    amount: number;
    currency: string;
    expireAt: Date | null;
};

export type OpenedRequests = {
    id: DossierId;
    destination: string;
    requestDate: Date | null;
    arrivalDate: Date | null;
    step: StepName;
};

export type TravellerInformation = {
    isRepeater: boolean;
    travellerId: number;
    openedRequests: OpenedRequests[];
    trips: TravellerTrip[];
};

export type TravellerTrip = {
    id: DossierId;
    destination: string;
    duration: number;
    hasTestimonial: boolean;
    year: number;
    budgetCurrency: string | null;
    budgetPerDayPerPax: number | null;
};

export type IncludedAvailableTasks = {
    type: 'available-tasks';
    id: DossierId;
    attributes: {
        bySteps: {
            [stepName in StepName]: TaskDefinition[];
        };
        suggested: { task_definition: TaskDefinition };
    };
    relationships: JsonApiRelationships;
};

export interface MemoFromAPI extends JsonApiObject {
    type: 'dossier-memo';
    id: string;
    attributes: {
        memo: string | null;
    };
}

export interface CustomerCareCrisisTicketFromAPI extends JsonApiObject {
    type: 'customer-care-crisis-ticket';
    id: string;
    attributes: {
        url: string;
    };
}

export interface ContractVouchersFromAPI extends JsonApiObject {
    type: 'contract-vouchers';
    id: string;
    attributes: {
        vouchers: Array<{
            id: string;
            filename: string;
        }>;
    };
}

export interface ContractDeferAgreementFromAPI extends JsonApiObject {
    type: 'contract-defer-agreement';
    id: string;
    attributes: {
        contractDeferAgreement: DeferAgreement;
    };
}

export interface TravellerInformationFromAPI extends JsonApiObject {
    type: 'traveller-information';
    id: string;
    attributes: {
        isRepeater: boolean;
        openedRequests: {
            id: number;
            destination: string;
            requestDate: string;
            arrivalDate: string;
            step: StepName;
        }[];
        trips: {
            id: number;
            destination: string;
            duration: number;
            hasTestimonial: boolean;
            year: number;
            budgetCurrency: string | null;
            budgetPerDayPerPax: number | null;
        }[];
    };
}

interface IncludedInitialRequest extends JsonApiObject {
    type: 'dossier-initial-request';
    id: string;
    attributes: {
        itinerary_url: string | null | undefined;
        summary: string | null | undefined;
    };
    relationships: JsonApiRelationships;
}

export interface IncludedParticipants extends JsonApiObject {
    type: 'dossier-participants';
    id: string;
    attributes: Participants;
    relationships: JsonApiRelationships;
}

type Relationships = 'available-tasks' | 'memo' | 'initial-request' | 'participants' | 'tags';

export type RelationshipValue = {
    type: IncludedResources;
    id: string;
};

export type RelationshipEntity = {
    [T in Relationships]: {
        data: RelationshipValue | RelationshipValue[];
    };
};

export type IncludedEntities =
    | IncludedAvailableTasks
    | MemoFromAPI
    | IncludedInitialRequest
    | IncludedParticipants
    | CustomerCareCrisisTicketFromAPI
    | ContractVouchersFromAPI
    | ContractDeferAgreementFromAPI
    | PreTripReviewFromAPI
    | InTripReviewFromAPI
    | TravellerInformationFromAPI
    | IncludedTag;

export type AvailableTasksFromAPI = {
    type: 'available-tasks';
    id: DossierId;
    attributes: {
        bySteps: {
            [step in StepName]: TaskDefinition[];
        };
        suggested: {
            task_definition: TaskDefinition;
        };
    };
    relationships: JsonApiRelationships;
};

export type DossierFromAPI = {
    type: 'dossier-overview';
    id: string;
    meta: {
        ['allowed-change-step-to']: StepNames;
    };
    attributes: {
        agent_id: number;
        archive_date: string | null | undefined;
        discussion: {
            last_traveller_to_agent_message_date: string | null | undefined;
            last_agent_to_traveller_message_date: string | null | undefined;
            last_message_read_date: string | null | undefined;
            has_response_required: boolean;
            has_unread_messages: boolean | null;
            last_traveller_unanswered_message_id: number | null;
            last_traveller_unanswered_message_content: string | null;
        };
        dossier_id: number;
        project: {
            approximate_arrival_date: string | null | undefined;
            approximate_duration_in_days: number | null | undefined;
            arrival_date: string | null | undefined;
            ideal_budget?: {
                amount_per_pax: number;
                currency: string;
                total_in_euro: number;
            } | null;
            max_total_in_euro?: number | null;
            departure_date: string | null | undefined;
            destination_label: string;
            market_code: string;
            pax_number: number | null | undefined;
            requester_id: number;
            requester_name: string;
            requester_phone: string | null | undefined;
            ab_mat_at1_insights?: string[] | null;
            ab_mat_at1_pace?: string | null;
            maturity_experiments?: {
                budget_range?: {
                    min: number;
                    max: number | null;
                } | null;
                ideal_budget?: number | null;
                max_budget?: number | null;
                booking?: Array<string>;
                departure_airport?: string;
                arrival_airport?: string;
                rooms?: string;
                flexibility?: string;
                rhythm?: string;
                is_latency_test?: boolean;
                at1_message_date?: string | null;
                trip_project_id?: string /* vvv Maturity AB test ab_at1_feedback vvv PR #2501 */;
            } | null;
        };
        rating: number;
        step: StepName;
        task: {
            action_type: string;
            close_date: string | null | undefined;
            definition: TaskDefinition;
            due_date: string;
            note: TaskNote;
        };
        request_date: string;
        testimonial_reception_date: string | null | undefined;
        contract: ContractFromDossier;
        requester_is_repeater: boolean;
        is_duplicate: boolean;
        duplicate_state: string | null;
        duplicate_from_dossier_id: number | null;
        heat_level: number;
    };
    relationships: RelationshipEntity;
};

export interface DossierJSONAPIResponse extends JsonApiResponse {
    data: DossierFromAPI;
    included?: IncludedEntities[];
}

type IncludedResources =
    | 'available-tasks'
    | 'dossier-memo'
    | 'dossier-initial-request'
    | 'dossier-participants'
    | 'customer-care-crisis-ticket'
    | 'contract-vouchers'
    | 'contract-defer-agreement'
    | 'pre-trip-review'
    | 'in-trip-review'
    | 'traveller-information'
    | 'tag';

export type DossierData = {
    dossier: DossierOverview;
    memo: Memo | null;
    initialRequest: InitialRequest | null;
    participants: Participants | null;
    availableTasks: AvailableTasks | null;
    suggestedTask: TaskDefinition | null;
    customerCareCrisisTicket: CustomerCareCrisisTicket | null;
    contractVouchers: Voucher[] | null;
    contractDeferAgreement: DeferAgreement | null;
    preTripReview: PreTripReview | null;
    inTripReview: InTripReview | null;
    travellerInformation: TravellerInformation | null;
};

// Selectors Types

export type DisplayDossierOverviews = DisplayDossierOverview[];

export type DisplayDossierOverview = DossierOverview & {
    memo: string | null | undefined;
    initialRequest: InitialRequest;
    availableTasks: AvailableTasks | null | undefined;
    participants: Participants | null | undefined;
    customerCareCrisisTicket: CustomerCareCrisisTicket | null | undefined;
    vouchers: Voucher[] | null | undefined;
    deferAgreement: DeferAgreement | null | undefined;
    preTripReview: PreTripReview | null | undefined;
    inTripReview: InTripReview | null | undefined;
    travellerInformation: TravellerInformation | null | undefined;
};

export type NormalizedMemo = {
    [dossierId in DossierId]: Memo;
};
export type NormalizedInitialRequest = {
    [dossierId in DossierId]: InitialRequest;
};
export type NormalizedParticipants = {
    [dossierId in DossierId]: Participants;
};

export type AvailableTasks = {
    [stepName in StepName]: TaskDefinition[];
};

export type NormalizedAvailableTasks = {
    [dossierId in DossierId]: {
        bySteps: AvailableTasks;
        suggested: TaskDefinition;
    };
};

export type NormalizedCustomerCareCrisisTicket = {
    [dossierId in DossierId]: CustomerCareCrisisTicket;
};

export type NormalizedVouchers = {
    [dossierId in DossierId]: Voucher[];
};

export type NormalizedDeferAgreement = {
    [dossierId in DossierId]: DeferAgreement;
};

export type NormalizedTravellerInformation = {
    [dossierId in DossierId]: TravellerInformation;
};

export type SerializedDossier = {
    id: DossierId;
    dossier: NormalizedDossier;
    dossierMemo?: NormalizedMemo;
    dossierInitialRequest?: NormalizedInitialRequest;
    dossierParticipants?: NormalizedParticipants;
    dossierAvailableTasks?: NormalizedAvailableTasks;
    dossierCustomerCareCrisisTicket?: NormalizedCustomerCareCrisisTicket;
    contractVouchers?: NormalizedVouchers;
    contractDeferAgreement?: NormalizedDeferAgreement;
    preTripReview?: NormalizedPreTripReview;
    inTripReview?: NormalizedInTripReview;
    travellerInformation?: NormalizedTravellerInformation;
};

export type SerializedDossiers = {
    dossier: NormalizedDossier;
};

export interface FormattedAgentForSelect {
    label: string;
    value: AgentId;
}

export type View = 'currentTask' | 'editDefinition' | 'editDueDate' | 'editNote';

export type MarketCode =
    | 'FR'
    | 'ES'
    | 'IT'
    | 'DE'
    | 'GB'
    | 'DK'
    | 'SE'
    | 'CH'
    | 'CA'
    | 'US'
    | 'NL'
    | 'AT';

/**
 * Active trip project field keys used in the frontend.
 *
 * Note: The backend may return additional legacy fields (quoteType, whyThisDestination,
 * otherDestinationsHesitation, travelPaces, neededDestinationInformation, decisionCriteria,
 * previouslyTraveledWithAgency, planToContactOtherAgencies, wishedExperiences, flightStatus).
 * These are intentionally excluded here as they are not currently displayed in the UI.
 *
 * See documentation/tech/initial-request-trip-project.md for full field list.
 */
export type TripProjectFieldKey =
    | 'projectStep'
    | 'project'
    | 'productRange'
    | 'hosting'
    | 'accompaniment'
    | 'guideLanguage'
    | 'itineraryUrl'
    | 'itineraryTitle';

export type FieldType = 'freeField' | 'boolean' | 'multipleChoice' | 'singleChoice';

export interface TripProjectFieldFromAPI extends JsonApiObject {
    type: 'tripProject';
    id: TripProjectFieldKey;
    attributes: {
        key: TripProjectFieldKey;
        keyTranslated: string;
        value: string | string[];
        valueTranslated: string | string[];
        fieldType: FieldType;
    };
}

export interface TripProjectJSONAPIResponse extends JsonApiResponse {
    data: TripProjectFieldFromAPI[];
}

type TripProjectFieldBase = {
    key: TripProjectFieldKey;
    keyTranslated: string;
};

type TripProjectFieldMultipleChoice = TripProjectFieldBase & {
    fieldType: 'multipleChoice';
    value: string[];
    valueTranslated: string[];
};

type TripProjectFieldSingle = TripProjectFieldBase & {
    fieldType: 'freeField' | 'boolean' | 'singleChoice';
    value: string;
    valueTranslated: string;
};

export type TripProjectField = TripProjectFieldMultipleChoice | TripProjectFieldSingle;

// Keys that are always multipleChoice (return array values)
type MultipleChoiceFieldKeys = Extract<
    TripProjectFieldKey,
    'productRange' | 'hosting' | 'guideLanguage'
>;

// Keys that are always single value (freeField, boolean, or singleChoice)
type SingleValueFieldKeys = Exclude<TripProjectFieldKey, MultipleChoiceFieldKeys>;

// Precise mapping of each field key to its correct type
export type SerializedTripProject = {
    [K in MultipleChoiceFieldKeys]?: TripProjectFieldMultipleChoice & { key: K };
} & {
    [K in SingleValueFieldKeys]?: TripProjectFieldSingle & { key: K };
};
